import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaPhone,
  FaEnvelope,
  FaUser,
  FaCalendarAlt,
  FaStickyNote,
  FaInfoCircle,
  FaUsers,
  FaHistory,
} from "react-icons/fa";
import { toast } from "react-toastify";

import {
  getLeadById,
  getLeadNotes,
  addLeadNote,
  getTeamManagers,
  getSalesUsers,
  getUsers,
} from "../../services/leadService";

import MasterLayout from "../../masterLayout/MasterLayout";
import "./ViewLead.css";

const ViewLead = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");

  const [tms, setTms] = useState([]);
  const [sales, setSales] = useState([]);
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [noteLoading, setNoteLoading] = useState(false);

  useEffect(() => {
    loadAll();
  }, [id]);

  /* =========================
     LOAD ALL DATA
  ========================= */
  const loadAll = async () => {
    try {
      await Promise.all([
        loadLead(),
        loadNotes(),
        loadLists(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadLead = async () => {
    const res = await getLeadById(id);
    if (res.status === "success") {
      setLead(res.data);
    } else {
      toast.error("Lead not found");
      navigate(-1);
    }
  };

  const loadNotes = async () => {
    const res = await getLeadNotes(id);
    if (res.status === "success") {
      setNotes(res.data || []);
    }
  };

  const loadLists = async () => {
    const tmRes = await getTeamManagers();
    const salesRes = await getSalesUsers();
    const userRes = await getUsers();

    setTms(tmRes?.data || []);
    setSales(salesRes?.data || []);
    setUsers(userRes?.data || []);
  };

  /* =========================
     ID → NAME HELPERS
  ========================= */
  const getTMName = (id) =>
    tms.find((t) => Number(t.id) === Number(id))?.name || "Unassigned";

  const getSalesName = (id) =>
    sales.find((s) => Number(s.id) === Number(id))?.name || "Unassigned";

  const getUserName = (id) =>
    users.find((u) => Number(u.id) === Number(id))?.name || "Unassigned";

  /* =========================
     NOTES
  ========================= */
  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error("Note cannot be empty");
      return;
    }

    setNoteLoading(true);

    const res = await addLeadNote({
      lead_id: id,
      note: newNote,
    });

    setNoteLoading(false);

    if (res.status === "success") {
      toast.success("Note added successfully");
      setNewNote("");
      loadNotes();
    } else {
      toast.error(res.message || "Failed to add note");
    }
  };

  const formatDateTime = (dt) => {
    if (!dt) return "-";
    const d = new Date(dt.replace(" ", "T"));
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (dt) => {
    if (!dt) return "-";
    const d = new Date(dt.replace(" ", "T"));
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <MasterLayout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading lead details...</p>
        </div>
      </MasterLayout>
    );
  }

  if (!lead) return null;

  return (
    <MasterLayout>
      <div className="lead-view">
        <div className="lead-view-container">

          {/* HEADER */}
          <div className="lead-header">
            <div className="header-left">
              <button onClick={() => navigate(-1)} className="btn-back">
                <FaArrowLeft /> Back to Leads
              </button>
              <div className="lead-title-section">
                <h4>{lead.name}</h4>
                <div className="lead-id">ID: #{lead.id}</div>
              </div>
            </div>
            <div className="status-badge-wrapper">
              <span className={`status-badge status-${lead.status?.toLowerCase()}`}>
                {lead.status}
              </span>
            </div>
          </div>

          {/* MAIN CONTENT GRID */}
          <div className="lead-content-grid">

            {/* LEFT COLUMN */}
            <div className="left-column">

              {/* CONTACT CARD */}
              <div className="info-card">
                <div className="card-header">
                  <FaUser className="card-icon" />
                  <h6>Contact Information</h6>
                </div>
                <div className="card-body">
                  <div className="contact-item">
                    <FaPhone className="contact-icon" />
                    <div>
                      <div className="contact-label">Phone</div>
                      <div className="contact-value">{lead.phone}</div>
                    </div>
                  </div>
                  <div className="contact-item">
                    <FaEnvelope className="contact-icon" />
                    <div>
                      <div className="contact-label">Email</div>
                      <div className="contact-value">
                        {lead.email || "Not provided"}
                      </div>
                    </div>
                  </div>
                  <div className="contact-item">
                    <FaInfoCircle className="contact-icon" />
                    <div>
                      <div className="contact-label">Source</div>
                      <div className="contact-value">
                        {lead.source || "Unknown"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ASSIGNMENT CARD (UI SAME) */}
              <div className="info-card">
                <div className="card-header">
                  <FaUsers className="card-icon" />
                  <h6>Assignment</h6>
                </div>
                <div className="card-body">
                  <div className="assignment-item">
                    <div className="assignment-label">Team Manager</div>
                    <div className="assignment-value">
                      {getTMName(lead.tm_id)}
                    </div>
                  </div>
                  <div className="assignment-item">
                    <div className="assignment-label">Sales Executive</div>
                    <div className="assignment-value">
                      {getSalesName(lead.sales_id)}
                    </div>
                  </div>
                  {/* <div className="assignment-item">
                    <div className="assignment-label">User ID</div>
                    <div className="assignment-value">
                      {getUserName(lead.user_id)}
                    </div>
                  </div> */}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="right-column">

              {/* FOLLOW UP CARD */}
              <div className="info-card">
                <div className="card-header">
                  <FaCalendarAlt className="card-icon" />
                  <h6>Follow-up Details</h6>
                </div>
                <div className="card-body">
                  <div className="followup-grid">
                    <div className="followup-item">
                      <div className="followup-label">Date</div>
                      <div className="followup-value">
                        {formatDate(lead.followup_date) || "Not scheduled"}
                      </div>
                    </div>
                    <div className="followup-item">
                      <div className="followup-label">Status</div>
                      <div className={`followup-status ${lead.followup_status?.toLowerCase() || "pending"}`}>
                        {lead.followup_status || "Pending"}
                      </div>
                    </div>
                    <div className="followup-item full-width">
                      <div className="followup-label">Discussion</div>
                      <div className="followup-value text-content">
                        {lead.discussion || "No discussion recorded"}
                      </div>
                    </div>
                    <div className="followup-item full-width">
                      <div className="followup-label">Remarks</div>
                      <div className="followup-value text-content">
                        {lead.remark || "No remarks"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* NOTES CARD */}
              <div className="info-card">
                <div className="card-header">
                  <FaStickyNote className="card-icon" />
                  <h6>Notes</h6>
                  <span className="notes-count">
                    {notes.length} note{notes.length !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="card-body">
                  <div className="note-input-section">
                    <textarea
                      placeholder="Type your note here..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      rows="3"
                    />
                    <div className="note-actions">
                      <button
                        className="btn-add-note"
                        onClick={handleAddNote}
                        disabled={noteLoading || !newNote.trim()}
                      >
                        {noteLoading ? "Adding..." : "Add Note"}
                      </button>
                    </div>
                  </div>

                  {notes.length === 0 ? (
                    <div className="empty-notes">
                      <FaStickyNote className="empty-icon" />
                      <p>No notes added yet</p>
                    </div>
                  ) : (
                    <div className="notes-list">
                      {notes.map((n) => (
                        <div className="note-item" key={n.id}>
                          <div className="note-content">
                            <p>{n.note}</p>
                            <div className="note-meta">
                              <span className="note-time">
                                {formatDateTime(n.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* META INFO */}
              <div className="info-card">
                <div className="card-header">
                  <FaHistory className="card-icon" />
                  <h6>Activity History</h6>
                </div>
                <div className="card-body">
                  <div className="meta-grid">
                    <div className="meta-item">
                      <div className="meta-label">Created At</div>
                      <div className="meta-value">
                        {formatDateTime(lead.created_at)}
                      </div>
                    </div>
                    <div className="meta-item">
                      <div className="meta-label">Updated At</div>
                      <div className="meta-value">
                        {formatDateTime(lead.updated_at)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default ViewLead;
